
import { type AxiosResponse } from 'axios';
import HttpClient from './httpClient';
import type { IHttpWrapperService } from './interface/IHttpWrapperService';
import type { ApiResponse } from '../models/ApiResponse';
import { appSettingsService } from './AppSettingsService';


export class HttpWrapperService implements IHttpWrapperService {
  private _httpClient: HttpClient | null = null;
  private _initPromise: Promise<void> | null = null;

  constructor() {
    // Initialize with a default client, will be replaced after settings load
    this._httpClient = new HttpClient("/api/");
    this._initPromise = this.initializeHttpClient();
  }

  private async initializeHttpClient(): Promise<void> {
    try {
      // Get API base URL from appsettings
      const settings = await appSettingsService.getSettings();
      let baseUrl = settings.Api.BaseUrl;

      // For Firebase hosting, use absolute URL to avoid CORS issues
      if (window.location.hostname.includes('firebase') || window.location.hostname.includes('web.app')) {
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/';
        }
      }

      // Replace the default client with the configured one
      this._httpClient = new HttpClient(baseUrl);
    } catch (error) {
      // Keep the fallback client already initialized
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (this._initPromise) {
      await this._initPromise;
    }
  }

  public getRequest = async <T>(resource: string): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    return this._httpClient!.get<ApiResponse<T>>(resource);
  };

  public postRequest = async <T, U>(resource: string, data?: U): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    return this._httpClient!.post<U, AxiosResponse<ApiResponse<T>>>(resource, data);
  };

  public putRequest = async <T, U>(resource: string, data?: U): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    return this._httpClient!.put<U, AxiosResponse<ApiResponse<T>>>(resource, data);
  };

  public deleteRequest = async <T, U>(resource: string, data?: U): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    return this._httpClient!.delete<ApiResponse<T>>(resource, { data });
  };

  public patchRequest = async <T, U>(resource: string, data?: U): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    return this._httpClient!.patch<U, AxiosResponse<ApiResponse<T>>>(resource, data);
  };

  public postBlobRequest = async <U>(resource: string, data?: U): Promise<AxiosResponse<Blob>> => {
    await this.ensureInitialized();
    return this._httpClient!.postBlob<U>(resource, data);
  };

  public getBlobRequest = async (resource: string): Promise<AxiosResponse<Blob>> => {
    await this.ensureInitialized();
    return this._httpClient!.get<Blob>(resource, { responseType: 'blob' });
  };

  public postFormDataRequest = async <T>(resource: string, formData: FormData): Promise<AxiosResponse<ApiResponse<T>>> => {
    await this.ensureInitialized();
    // Don't set Content-Type header - let the browser set it automatically with the boundary
    return this._httpClient!.post<FormData, AxiosResponse<ApiResponse<T>>>(resource, formData);
  };
}